import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AddFamilyPage.css';

const MAX_FAMILY_MEMBERS = 4;

const AddFamilyPage = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', age: '', gender: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  // DB-driven member count
  const [memberCount, setMemberCount] = useState(0);
  const [countLoading, setCountLoading] = useState(true);

  /* ── Fetch current member count from DB on mount ── */
  useEffect(() => {
    const fetchMemberCount = async () => {
      try {
        const pnid = localStorage.getItem('pnid');
        if (!pnid) { setCountLoading(false); return; }

        const response = await fetch('https://backend-coupon-distribution-nhf.onrender.com/family-number', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ number: pnid }),
        });

        if (!response.ok) throw new Error('Failed to fetch');

        const result = await response.json();

        if (result.success && result.data) {
          setMemberCount(result.data.length);
        }
      } catch (err) {
        console.error('❌ Error fetching member count:', err);
      } finally {
        setCountLoading(false);
      }
    };

    fetchMemberCount();
  }, []);

  const isLimitReached = memberCount >= MAX_FAMILY_MEMBERS;

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (form.age && (isNaN(form.age) || Number(form.age) < 1 || Number(form.age) > 120))
      e.age = 'Enter a valid age';
    if (form.phone && !/^\d{10}$/.test(form.phone.trim()))
      e.phone = 'Enter a valid 10-digit phone number';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (apiError) setApiError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Re-check limit against current DB count
    if (memberCount >= MAX_FAMILY_MEMBERS) {
      setApiError(`You can only add up to ${MAX_FAMILY_MEMBERS} family members.`);
      return;
    }

    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const receiver_pnid = localStorage.getItem('pnid');
    if (!receiver_pnid) {
      setApiError('Session expired. Please log in again.');
      return;
    }

    setLoading(true);
    setApiError('');

    try {
      const response = await fetch('https://backend-coupon-distribution-nhf.onrender.com/add-member', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          receiver_pnid,
          name: form.name.trim(),
          age: form.age ? Number(form.age) : null,
          gender: form.gender || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setApiError(data.error || 'Something went wrong. Please try again.');
        return;
      }

      // Update local count to reflect the new addition
      setMemberCount((prev) => prev + 1);

      // Also save locally so dashboard reflects immediately
      const saved = localStorage.getItem('nutrihealth_added_family_details');
      const existing = saved ? JSON.parse(saved) : [];
      const newMember = {
        id: `f${data.insertedId || Date.now()}`,
        name: form.name.trim(),
        relation: `Member ${existing.length + 1}`,
        relKey: `member${existing.length + 1}`,
        couponStatus: 'assigned',
        phone: form.phone.trim(),
        age: form.age,
        gender: form.gender,
        date: `Added on ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`,
      };
      localStorage.setItem(
        'nutrihealth_added_family_details',
        JSON.stringify([...existing, newMember])
      );

      setSubmitted(true);
    } catch (err) {
      setApiError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnother = () => {
    if (memberCount >= MAX_FAMILY_MEMBERS) return;
    setSubmitted(false);
    setForm({ name: '', age: '', gender: '', phone: '' });
    setErrors({});
    setApiError('');
  };

  /* ── Loading count ── */
  if (countLoading) {
    return (
      <div className="af-root">
        <div className="af-container fade-in-up" style={{ textAlign: 'center', paddingTop: '4rem' }}>
          <span className="af-spinner" />
          <p style={{ marginTop: '1rem', color: 'var(--text-muted, #888)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  /* ── Limit Reached Screen ── */
  if (isLimitReached) {
    return (
      <div className="af-root">
        <div className="af-success fade-in-up">
          <div className="af-success-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>

          <h2 className="af-success-title">Limit Reached</h2>
          {/* <p className="af-success-sub">
            You've already added <strong>{MAX_FAMILY_MEMBERS} family members</strong>, which is the maximum allowed.
          </p> */}

          <div className="af-coupon-notice">
            <div className="af-coupon-notice-icon">👨‍👩‍👧‍👦</div>
            <div>
              <p className="af-coupon-notice-title">Family Plan Full</p>
              <p className="af-coupon-notice-desc">
                All {MAX_FAMILY_MEMBERS} slots have been used. View your family members on the dashboard.
              </p>
            </div>
          </div>

          <div className="af-success-actions">
            <button className="btn-primary" onClick={() => navigate('/dashboard')} id="back-dashboard-btn">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Success Screen ── */
  if (submitted) {
    const remainingSlots = MAX_FAMILY_MEMBERS - memberCount;

    return (
      <div className="af-root">
        <div className="af-success fade-in-up">

          <div className="af-success-icon">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h2 className="af-success-title">Member Added!</h2>
          <p className="af-success-sub">
            <strong>{form.name}</strong> has been added to your family plan.
          </p>

          <div className="af-coupon-notice">
            <div className="af-coupon-notice-icon">🎟️</div>
            <div>
              <p className="af-coupon-notice-title">Coupon Auto-Assigned</p>
              <p className="af-coupon-notice-desc">
                {form.phone.trim()
                  ? `Vitamin D Test coupons has been assigned to the member.View more details on the dashboard.`
                  : 'Vitamin D Test coupons has been assigned to the member.View more details on the dashboard.'}
              </p>
            </div>
          </div>

          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted, #888)', margin: '0 0 1rem' }}>
            {remainingSlots > 0
              ? `${remainingSlots} slot${remainingSlots > 1 ? 's' : ''} remaining out of ${MAX_FAMILY_MEMBERS}`
              : `You've used all ${MAX_FAMILY_MEMBERS} slots`}
          </p>

          <div className="af-success-actions">
            <button className="btn-primary" onClick={() => navigate('/dashboard')} id="back-dashboard-btn">
              Back to Dashboard
            </button>
            {remainingSlots > 0 && (
              <button className="btn-outline af-add-another" onClick={handleAddAnother} id="add-another-btn">
                Add Another
              </button>
            )}
          </div>

        </div>
      </div>
    );
  }

  /* ── Form ── */
  return (
    <div className="af-root">
      <div className="af-container fade-in-up">

        {/* Back */}
        <button className="back-link" onClick={() => navigate('/dashboard')} id="back-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>

        {/* Page title */}
        <div className="af-page-title">
          <div className="af-title-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="23" y1="11" x2="17" y2="11" />
              <line x1="20" y1="8" x2="20" y2="14" />
            </svg>
          </div>
          <div>
            <h1 className="af-title">Add Family Member</h1>
            <p className="af-subtitle">
              Fill in the details to assign them a coupon &nbsp;·&nbsp;
              <strong>{MAX_FAMILY_MEMBERS - memberCount} slot{MAX_FAMILY_MEMBERS - memberCount !== 1 ? 's' : ''} left</strong>
            </p>
          </div>
        </div>

        {/* Form card */}
        <div className="af-card">
          <h3 className="af-card-title">Member Details</h3>

          {apiError && (
            <div className="af-api-error">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {apiError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="af-form">

            {/* Name */}
            <div className="form-group">
              <label className="form-label" htmlFor="af-name">Full Name *</label>
              <input
                id="af-name"
                name="name"
                type="text"
                className={`form-input ${errors.name ? 'form-input--error' : ''}`}
                placeholder="Enter full name"
                value={form.name}
                onChange={handleChange}
                disabled={loading}
              />
              {errors.name && <p className="af-error">{errors.name}</p>}
            </div>

            {/* Age + Gender */}
            <div className="af-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label" htmlFor="af-age">
                  Age <span className="af-optional">(Optional)</span>
                </label>
                <input
                  id="af-age"
                  name="age"
                  type="number"
                  min="1"
                  max="120"
                  className={`form-input ${errors.age ? 'form-input--error' : ''}`}
                  placeholder="Age"
                  value={form.age}
                  onChange={handleChange}
                  disabled={loading}
                />
                {errors.age && <p className="af-error">{errors.age}</p>}
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label" htmlFor="af-gender">
                  Gender <span className="af-optional">(Optional)</span>
                </label>
                <select
                  id="af-gender"
                  name="gender"
                  className="form-select"
                  value={form.gender}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Phone */}
            <div className="form-group">
              <label className="form-label" htmlFor="af-phone">
                Phone <span className="af-optional">(Optional)</span>
              </label>
              <div className="af-phone-wrap">
                <span className="af-phone-prefix">+91</span>
                <input
                  id="af-phone"
                  name="phone"
                  type="tel"
                  className={`form-input af-phone-input ${errors.phone ? 'form-input--error' : ''}`}
                  placeholder="10-digit mobile number"
                  value={form.phone}
                  onChange={handleChange}
                  maxLength={10}
                  disabled={loading}
                />
              </div>
              {errors.phone && <p className="af-error">{errors.phone}</p>}
              <p className="af-phone-hint">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
                {/* Coupon will be sent via WhatsApp if provided */}
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn-primary af-submit-btn"
              id="submit-family-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="af-spinner" />
                  Adding Member...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="23" y1="11" x2="17" y2="11" />
                    <line x1="20" y1="8" x2="20" y2="14" />
                  </svg>
                  Add Member &amp; Assign Coupon
                </>
              )}
            </button>

          </form>
        </div>

      </div>
    </div>
  );
};

export default AddFamilyPage;